// ts-rest contract for the governance programme tracker (PRD #73).
//
// Covers two related surfaces sharing one data model:
//   - ManComm programme tracker (meeting_kind = 'mancomm')
//   - IFC governance            (meeting_kind = 'ifc')
//
// Endpoints under /api/governance/*:
//   GET  /api/governance/items                    — list programme items
//   GET  /api/governance/items/:id                — single item detail
//   POST /api/governance/item/:id/status          — inline RAG status update
//   GET  /api/governance/meetings                 — list meetings
//   GET  /api/governance/meetings/:id             — single meeting + agenda
//   GET  /api/governance/papers                   — list materialised papers
//   GET  /api/governance/drift                    — stale-amber drift scan
//
// All endpoints scope to a meeting_kind via query / row field. The state
// machine that gates RAG transitions lives in
// apps/web/src/lib/governance-state-machine.ts (web) and is re-implemented
// next to the server mutator in governance-state.ts.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

/** The two governance forums this module tracks (board reuses the schema). */
export const governanceMeetingKindSchema = z.enum(['mancomm', 'ifc', 'board']);
export type GovernanceMeetingKind = z.infer<typeof governanceMeetingKindSchema>;

/** RAG status for a programme item. Lowercase — matches the ontology object. */
export const governanceRagSchema = z.enum(['green', 'amber', 'red']);
export type GovernanceRag = z.infer<typeof governanceRagSchema>;

/** Delivery stage for a programme item. */
export const governanceStageSchema = z.enum(['idea', 'scoping', 'delivery', 'review', 'landed']);
export type GovernanceStage = z.infer<typeof governanceStageSchema>;

/** Open / closed lifecycle — closed items drop off the meeting agenda. */
export const governanceItemStatusSchema = z.enum(['open', 'closed']);
export type GovernanceItemStatus = z.infer<typeof governanceItemStatusSchema>;

/** Decision outcome logged at a meeting. */
export const governanceDecisionOutcomeSchema = z.enum([
    'approved',
    'rejected',
    'deferred',
    'noted',
]);
export type GovernanceDecisionOutcome = z.infer<typeof governanceDecisionOutcomeSchema>;

// ---------------------------------------------------------------------------
// Programme item
// ---------------------------------------------------------------------------

/** One audit entry on a programme item's status-change history. */
export const governanceItemAuditEntrySchema = z.object({
    id: z.string(),
    at: z.string(),
    by: z.string(),
    /** RAG before the change — null for the seed/creation entry. */
    fromRag: governanceRagSchema.nullable(),
    toRag: governanceRagSchema,
    note: z.string(),
});
export type GovernanceItemAuditEntry = z.infer<typeof governanceItemAuditEntrySchema>;

export const governanceProgrammeItemSchema = z.object({
    id: z.string(),
    meetingKind: governanceMeetingKindSchema,
    title: z.string(),
    segment: z.string(),
    theme: z.string(),
    owner: z.string(),
    rag: governanceRagSchema,
    stage: governanceStageSchema,
    status: governanceItemStatusSchema,
    dueDate: z.string(),
    /** ISO timestamp the item entered its current RAG. Drives the drift scan. */
    currentStatusSince: z.string(),
    lastUpdate: z.string(),
    description: z.string(),
    relatedDocuments: z.array(z.string()),
    comments: z.array(z.object({ at: z.string(), by: z.string(), text: z.string() })),
    auditLog: z.array(governanceItemAuditEntrySchema),
});
export type GovernanceProgrammeItem = z.infer<typeof governanceProgrammeItemSchema>;

// ---------------------------------------------------------------------------
// Meeting / decisions
// ---------------------------------------------------------------------------

export const governanceDecisionSchema = z.object({
    id: z.string(),
    meetingId: z.string(),
    title: z.string(),
    outcome: governanceDecisionOutcomeSchema,
    detail: z.string(),
    decidedBy: z.string(),
    decidedAt: z.string(),
    /** Programme item this decision relates to, if any. */
    itemId: z.string().nullable(),
});
export type GovernanceDecision = z.infer<typeof governanceDecisionSchema>;

export const governanceMeetingSchema = z.object({
    id: z.string(),
    meetingKind: governanceMeetingKindSchema,
    title: z.string(),
    meetingDate: z.string(),
    chair: z.string(),
    status: z.enum(['scheduled', 'held']),
});
export type GovernanceMeeting = z.infer<typeof governanceMeetingSchema>;

/** An agenda line auto-derived for an upcoming meeting. */
export const governanceAgendaLineSchema = z.object({
    itemId: z.string(),
    title: z.string(),
    segment: z.string(),
    owner: z.string(),
    rag: governanceRagSchema,
    dueDate: z.string(),
    /** Why the line is on the agenda. */
    reason: z.enum(['red', 'amber', 'due_soon']),
});
export type GovernanceAgendaLine = z.infer<typeof governanceAgendaLineSchema>;

// ---------------------------------------------------------------------------
// Papers (durable post-FORUM)
// ---------------------------------------------------------------------------

export const governancePaperSchema = z.object({
    id: z.string(),
    paperRef: z.string(),
    meetingKind: governanceMeetingKindSchema,
    meetingDate: z.string(),
    executiveSummary: z.string(),
    ragStatus: z.array(
        z.object({
            segment: z.string(),
            rag: governanceRagSchema,
            note: z.string(),
        }),
    ),
    decisionsPending: z.array(z.string()),
    status: z.string(),
    generatedAt: z.string(),
});
export type GovernancePaper = z.infer<typeof governancePaperSchema>;

// ---------------------------------------------------------------------------
// Drift scan
// ---------------------------------------------------------------------------

export const staleAmberFlagSchema = z.object({
    itemId: z.string(),
    title: z.string(),
    segment: z.string(),
    owner: z.string(),
    daysAmber: z.number().int(),
    amberSince: z.string(),
});
export type StaleAmberFlag = z.infer<typeof staleAmberFlagSchema>;

export const governanceDriftResponseSchema = z.object({
    scannedAt: z.string(),
    thresholdDays: z.number().int(),
    flags: z.array(staleAmberFlagSchema),
});
export type GovernanceDriftResponse = z.infer<typeof governanceDriftResponseSchema>;

// ---------------------------------------------------------------------------
// Request / response
// ---------------------------------------------------------------------------

export const governanceItemsResponseSchema = z.object({
    items: z.array(governanceProgrammeItemSchema),
});
export type GovernanceItemsResponse = z.infer<typeof governanceItemsResponseSchema>;

export const governanceItemResponseSchema = z.object({
    ok: z.boolean(),
    item: governanceProgrammeItemSchema,
});
export type GovernanceItemResponse = z.infer<typeof governanceItemResponseSchema>;

export const governanceMeetingsResponseSchema = z.object({
    meetings: z.array(governanceMeetingSchema),
});
export type GovernanceMeetingsResponse = z.infer<typeof governanceMeetingsResponseSchema>;

export const governanceMeetingDetailResponseSchema = z.object({
    meeting: governanceMeetingSchema,
    agenda: z.array(governanceAgendaLineSchema),
    /** Decisions logged at the most recent prior meeting of the same kind. */
    priorDecisions: z.array(governanceDecisionSchema),
});
export type GovernanceMeetingDetailResponse = z.infer<typeof governanceMeetingDetailResponseSchema>;

export const governancePapersResponseSchema = z.object({
    papers: z.array(governancePaperSchema),
});
export type GovernancePapersResponse = z.infer<typeof governancePapersResponseSchema>;

export const statusUpdateRequestSchema = z.object({
    rag: governanceRagSchema,
    note: z.string().min(1, 'A note is required for every status change'),
    by: z.string(),
    /** Set when reopening a closed item — requires a supervisor override. */
    override: z.boolean().optional(),
});
export type StatusUpdateRequest = z.infer<typeof statusUpdateRequestSchema>;

const errorBody = z.object({ error: z.string(), code: z.string().optional() });

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const governanceContract = c.router(
    {
        listItems: {
            method: 'GET',
            path: '/governance/items',
            query: z
                .object({
                    meetingKind: governanceMeetingKindSchema.optional(),
                    segment: z.string().optional(),
                    owner: z.string().optional(),
                    rag: governanceRagSchema.optional(),
                })
                .optional(),
            responses: { 200: governanceItemsResponseSchema },
            summary: 'List governance programme items',
        },
        getItem: {
            method: 'GET',
            path: '/governance/items/:id',
            responses: { 200: governanceItemResponseSchema, 404: errorBody },
            summary: 'Get a single programme item with full audit history',
        },
        updateStatus: {
            method: 'POST',
            path: '/governance/item/:id/status',
            body: statusUpdateRequestSchema,
            responses: {
                200: governanceItemResponseSchema,
                400: errorBody,
                404: errorBody,
                409: errorBody,
            },
            summary: 'Update a programme item RAG status (writes an audit row)',
        },
        listMeetings: {
            method: 'GET',
            path: '/governance/meetings',
            query: z.object({ meetingKind: governanceMeetingKindSchema.optional() }).optional(),
            responses: { 200: governanceMeetingsResponseSchema },
            summary: 'List governance meetings',
        },
        getMeeting: {
            method: 'GET',
            path: '/governance/meetings/:id',
            responses: {
                200: governanceMeetingDetailResponseSchema,
                404: errorBody,
            },
            summary: 'Get a meeting with auto-derived agenda + prior decisions',
        },
        listPapers: {
            method: 'GET',
            path: '/governance/papers',
            query: z.object({ meetingKind: governanceMeetingKindSchema.optional() }).optional(),
            responses: { 200: governancePapersResponseSchema },
            summary: 'List materialised governance papers',
        },
        drift: {
            method: 'GET',
            path: '/governance/drift',
            query: z.object({ meetingKind: governanceMeetingKindSchema.optional() }).optional(),
            responses: { 200: governanceDriftResponseSchema },
            summary: 'Scan for items amber beyond the drift threshold (21 days)',
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
